import type { DestinationOption } from '@/features/pipelines/destinations';
import { DestinationMode } from '@/features/pipelines/destinations/types';
import type { Source } from '@/features/pipelines/sources';
import type { Destination } from '@/features/pipelines/destinations';
import type { SourceOption } from '@/features/pipelines/sources';
import { SourceType } from '@/features/pipelines/sources/types';

export const parseCategories = (categoryField?: string) => {
  const categories =
    categoryField == null || categoryField.length === 0
      ? ['Other']
      : categoryField
          .split(',')
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
  return categories.length > 0 ? categories : ['Other'];
};

const DESTINATION_MODES_LABEL: Record<DestinationMode, string> = {
  [DestinationMode.Javascript]: 'Javascript',
  [DestinationMode.Direct]: 'Direct',
  [DestinationMode.Cloud]: 'Cloud',
  [DestinationMode.StaticEtl]: 'ETL',
};

export const destinationModeLabel = (mode?: string) => {
  if (mode == null) {
    return '-';
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return DESTINATION_MODES_LABEL[mode as DestinationMode] ?? mode;
};

export const JAVASCRIPT_SOURCE_ONLY_MSG = `
  This destination only supports JavaScript sources. Data is sent directly
  from the JavaScript source to the destination, without passing through
  Customer.io’s servers.
  `;
const JOURNEYS_MESSAGE_METRICS_SOURCE_MSG = `
  The message metrics data comes from this workspace.
  Create a new Customer.io Data Out integration if you want to use this data in another workspace.
`;

export function checkSourceAndDestinationCompatibility(
  source: Partial<Pick<Source, 'option_id' | 'is_journeys_metrics_source'>>,
  destination: Partial<Pick<Destination, 'mode' | 'has_cio_destinations'>>,
): { isCompatible: boolean; reason?: string } {
  // Direct/Web destinations are only compatible with JavaScript sources
  if (
    destination.mode != null &&
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    ([DestinationMode.Direct, DestinationMode.Javascript] as DestinationMode[]).includes(destination.mode)
  ) {
    return source.option_id === 'javascript'
      ? { isCompatible: true }
      : { isCompatible: false, reason: JAVASCRIPT_SOURCE_ONLY_MSG };
  }

  // Message metrics source are not compatible with the auto-track destination
  if (source.is_journeys_metrics_source == true && destination.has_cio_destinations == true) {
    return { isCompatible: false, reason: JOURNEYS_MESSAGE_METRICS_SOURCE_MSG };
  }

  return { isCompatible: true };
}

export function isDatabaseSource(definition: SourceOption) {
  return definition.type === SourceType.DATABASE;
}

export function isEtlDestination(destination?: { category?: string }) {
  return destination?.category?.includes('Cloud Storage');
}

export function isStaticEtlDestination(destination?: DestinationOption) {
  return destination?.mode == 'static-etl';
}

export function isSalesforceDestination(destination?: DestinationOption | null) {
  return destination?.option_id == 'salesforce';
}

export function isJourneysAutoTrackDestination(destination?: { option_id: string; has_cio_destinations: boolean }) {
  return destination?.option_id === 'customerio' && destination?.has_cio_destinations;
}

export function canDisconnectSourceFromDestination(source: Source, destination: Destination) {
  return !source.is_journeys_auto_track_source || !destination.has_cio_destinations;
}

export function isConnectorSource(source?: Pick<SourceOption, 'type'>) {
  return source?.type === SourceType.CONNECTOR;
}

export function hasOverviewData(source?: SourceOption): boolean {
  if (source == null) return false;
  return (
    source.integration_description != null ||
    source.features != null ||
    source.requirements != null ||
    (source.integration_metadata != null && source.integration_metadata.length > 0)
  );
}

export function isOptionIDConnectorSource(optionID: string) {
  return ['salesforce', 'hubspot'].includes(optionID);
}
